import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import styled from 'styled-components';
import { GdprNotice } from '@/common/GdprNotice';
import { FollowUpVerifyModal } from '@/follow-up/FollowUpVerifyModal';
import { PrimaryFooter } from '@/footer/PrimaryFooter';
import AppBar from '@/header/AppBar';
import PrimaryHeaderBar from '@/header/PrimaryHeaderBar';
import { OnboardingModal } from '@/onboarding/OnboardingModal';
import { useOnboarding } from '~/hooks/useOnboarding';

function RouteComponent() {
  const { isOnboardingOpen, onClose, pendingId } = useOnboarding();

  // Get the page bg color from route context
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const siteBG = (currentMatch?.context as any)?.pageBackground || 'default';
  const noContainer = (currentMatch?.context as any)?.noContainer || false; // Allows for full-width elements on pages
  const noOverflowHidden =
    (currentMatch?.context as any)?.noOverflowHidden || false; // Allows for sticky positioning on scroll

  return (
    <Site data-page-background={siteBG} $noOverflowHidden={noOverflowHidden}>
      <AppBar />
      <PrimaryHeaderBar />
      <Content>
        {noContainer ? (
          <Outlet />
        ) : (
          <Container>
            <Outlet />
          </Container>
        )}
      </Content>
      <PrimaryFooter />
      <GdprNotice />
      <OnboardingModal
        isOpen={isOnboardingOpen}
        onClose={onClose}
        onboardingId={pendingId}
      />
      {/* Site-wide, like legacy's `wp_footer` hook, and gated entirely inside
          `useFollowUpVerification` — including on the onboarding queue being
          empty, which is why it sits after `OnboardingModal` rather than being
          driven by it. */}
      <FollowUpVerifyModal />
    </Site>
  );
}

export const Route = createFileRoute('/_app')({
  component: RouteComponent,
});

const Site = styled.div<{ $noOverflowHidden?: boolean }>`
  background: var(--bg-default);
  display: flex;
  flex-direction: column;
  font-family:
    'Raleway',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  min-height: 100vh;
  overflow-x: ${(props) => (props.$noOverflowHidden ? 'visible' : 'hidden')};
  overflow-y: visible;

  &[data-page-background='white'] {
    background: var(--white);
  }
`;

const Content = styled.div`
  background: inherit;
  min-height: 70vh;
`;

const Container = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: 1385px;
  padding: 0 20px;
`;
